import type { ReactNode } from 'react';
import { cn } from '@/lib/format.ts';

/** The shell every screen that is not the board shares. */
export function Page({
  title,
  blurb,
  actions,
  children,
  wide,
}: {
  title: string;
  blurb?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <main className="min-h-0 min-w-0 flex-1 overflow-y-auto lg:col-span-2">
      <div
        className={cn(
          'mx-auto px-4 pt-5 pb-16 sm:px-6 sm:pt-6 lg:px-8',
          wide ? 'max-w-5xl' : 'max-w-3xl',
        )}
      >
        <div className="flex flex-wrap items-start gap-x-4 gap-y-3 border-b border-line pb-4">
          {/*
            Full width below `sm`, for the same reason as Row's title: a
            shrinkable flex-1 title beside shrink-0 actions squeezes itself to
            a sliver on a phone instead of letting the actions wrap below.
          */}
          <div className="w-full min-w-0 sm:w-auto sm:flex-1">
            <h1 className="m-0 text-[21px] font-[650] tracking-[-0.01em]">{title}</h1>
            {blurb ? (
              <p className="mt-1.5 max-w-2xl text-[13.5px] leading-relaxed text-body">{blurb}</p>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
        </div>
        <div className="pt-5">{children}</div>
      </div>
    </main>
  );
}

export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    // Unbordered and edge-flush below `sm` — a phone screen is narrow enough
    // that a bordered box inside it reads as a frame around a frame (the page
    // margin is already one). The border and corner rounding are things a
    // wider page has room to spend; a phone spends that width on content.
    <div className={cn('bg-panel px-4 py-4 sm:rounded-2xl sm:border sm:border-line sm:p-5', className)}>
      {children}
    </div>
  );
}

export function Row({
  title,
  subtitle,
  meta,
  actions,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-line py-3.5 last:border-b-0">
      <div className="flex flex-wrap items-center gap-3">
        {/*
          Full width below `sm`, not `flex-1`: a flexible, shrinkable title
          sitting next to shrink-0 meta/actions just shrinks itself toward
          zero on a narrow phone rather than wrapping — flex-wrap only moves
          an item to its own line when nothing satisfies the row by shrinking,
          and a min-w-0 flex-1 item always "fits" by shrinking. Full width
          forces meta/actions onto their own line below instead, so title
          keeps enough room for its own truncation to mean something.
        */}
        <div className="min-w-0 w-full sm:w-auto sm:flex-1">
          <div className="truncate text-[14px] font-semibold text-t1">{title}</div>
          {subtitle ? (
            <div className="mt-0.5 truncate text-[12.5px] text-mute">{subtitle}</div>
          ) : null}
        </div>
        {meta ? <div className="shrink-0 text-[12px] text-mute">{meta}</div> : null}
        {actions ? <div className="flex shrink-0 gap-2">{actions}</div> : null}
      </div>
      {children}
    </div>
  );
}
